import { api } from '../../core/api.js';
import { DOC_PAGE, DOC_RECENT, MEMORY_PAGE, countsOf } from './paging.js';

export function memoryListQuery(productId, spec) {
  const params = new URLSearchParams();
  params.set('product_id', productId);
  params.set('limit', String(spec.limit));
  params.set('offset', String(spec.offset || 0));
  if (spec.memoryType) params.set('memory_type', spec.memoryType);
  const q = String(spec.q || '').trim();
  if (q) params.set('q', q);
  if (spec.permanence) params.set('permanence', String(spec.permanence));
  if (spec.includeExpired) params.set('include_expired', '1');
  return `/context/memory?${params}`;
}

export function documentListQuery(productId, spec) {
  const params = new URLSearchParams();
  params.set('product_id', productId);
  params.set('fields', 'list');
  params.set('limit', String(spec.limit));
  params.set('offset', String(spec.offset || 0));
  const q = String(spec.q || '').trim();
  if (q) params.set('q', q);
  return `/documents?${params}`;
}

export function fetchMemoryPage(productId, spec) {
  return api(memoryListQuery(productId, {
    limit: spec.limit || MEMORY_PAGE,
    offset: spec.offset || 0,
    memoryType: spec.type || '',
    q: spec.q || '',
    permanence: spec.permanence || '',
    includeExpired: !!spec.includeExpired,
  }));
}

export function fetchDocumentPage(productId, spec) {
  return api(documentListQuery(productId, {
    limit: spec.limit || DOC_PAGE,
    offset: spec.offset || 0,
    q: spec.q || '',
  }));
}

function readMemory(result) {
  if (!result || result.error) {
    return {
      error: (result && result.error) || 'Could not load',
      rows: [],
      total: 0,
      counts: null,
      newest: null,
    };
  }
  const rows = result.memories || [];
  const total = Number(result.total);
  return {
    error: '',
    rows,
    total: Number.isFinite(total) ? total : rows.length,
    counts: result.counts ? countsOf(result) : null,
    newest: result.newest_at || null,
  };
}

function readDocs(result) {
  if (!result || result.error) {
    return { error: (result && result.error) || 'Could not load', rows: [], total: 0 };
  }
  const rows = result.documents || [];
  const total = Number(result.total);
  return { error: '', rows, total: Number.isFinite(total) ? total : rows.length };
}

/** @param {string} productId @param {{ documentsTab?: boolean }} [opts] */
export async function loadKnowledge(productId, opts = {}) {
  const query = `product_id=${encodeURIComponent(productId)}`;
  const docLimit = opts.documentsTab ? DOC_PAGE : DOC_RECENT;
  const [memory, instructions, documents, depth, connectors] = await Promise.all([
    fetchMemoryPage(productId, { type: 'fact', limit: MEMORY_PAGE, offset: 0 }),
    fetchMemoryPage(productId, { type: 'instruction', limit: MEMORY_PAGE, offset: 0 }),
    fetchDocumentPage(productId, { limit: docLimit, offset: 0 }),
    api(`/connectors/depth?${query}`),
    api(`/connectors?${query}`),
  ]);
  const facts = readMemory(memory);
  const standing = readMemory(instructions);
  const docs = readDocs(documents);
  const counts = facts.counts || {
    fact: facts.rows.length,
    decision: 0,
    todo: 0,
    instruction: standing.total,
  };
  return {
    memory: facts.error ? 'error' : 'ready',
    memoryError: facts.error,
    memoryLists: {
      fact: { rows: facts.rows, total: facts.total, loaded: !facts.error },
      decision: { rows: [], total: counts.decision, loaded: false },
      todo: { rows: [], total: counts.todo, loaded: false },
    },
    memoryCounts: counts,
    newest: facts.newest,
    instructions: standing.rows,
    instructionTotal: standing.total,
    instructionStatus: standing.error ? 'error' : 'ready',
    instructionError: standing.error,
    documents: docs.error ? 'error' : 'ready',
    documentsError: docs.error,
    docs: docs.rows,
    docsTotal: docs.total,
    docsMode: opts.documentsTab ? 'page' : 'recent',
    depth: depth && !depth.error ? 'ready' : 'error',
    depthError: depth && depth.error ? depth.error : '',
    breakdown: depth && depth.depth_breakdown ? depth.depth_breakdown : {},
    connectors: connectors && !connectors.error ? 'ready' : 'error',
    connectorsError: connectors && connectors.error ? connectors.error : '',
    sources: connectors && !connectors.error ? (connectors.integrations || []) : [],
  };
}
